import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { User, FileText, Package, ChevronRight, ChevronLeft, Check } from "lucide-react";

const STEPS = [
  { id: 1, title: "Create Profile", icon: User, desc: "Basic KYC information" },
  { id: 2, title: "Upload Documents", icon: FileText, desc: "PAN & Aadhaar cards" },
  { id: 3, title: "Select Package", icon: Package, desc: "Choose your service" },
];

const OnboardingWizard = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({ full_name: "", pan_number: "", aadhar_number: "", dob: "", phone: "" });
  const [createdProfileId, setCreatedProfileId] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState("");

  const handleCreateProfile = async () => {
    if (!profileData.full_name || !profileData.pan_number) {
      toast.error("Name and PAN number are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: profileData.full_name,
        pan_number: profileData.pan_number,
        aadhar_number: profileData.aadhar_number,
        date_of_birth: profileData.dob || null,
        phone: profileData.phone || "",
      };
      const res = await authFetch(`${API}/profiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to create profile");
      const data = await res.json();
      setCreatedProfileId(data.profile_id);
      toast.success("Profile created!");
      setStep(2);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocs = async () => {
    if (!panFile && !aadharFile) {
      toast.info("You can skip this step if you don't have documents ready.");
      setStep(3);
      return;
    }
    setLoading(true);
    try {
      for (const [file, docType] of [[panFile, "pan"], [aadharFile, "aadhar"]]) {
        if (file && createdProfileId) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("doc_type", docType);
          const res = await authFetch(`${API}/profiles/${createdProfileId}/upload-kyc`, {
            method: "POST",
            body: fd,
          });
          if (!res.ok) throw new Error(`Failed to upload ${docType === "pan" ? "PAN Card" : "Aadhaar Card"}`);
        }
      }
      toast.success("Documents uploaded!");
      setStep(3);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = async () => {
    if (!selectedPackage) {
      toast.error("Please select a service");
      return;
    }
    setLoading(true);
    try {
      await authFetch(`${API}/client-service-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service_type: selectedPackage, profile_id: createdProfileId }),
      });
      await authFetch(`${API}/auth/complete-onboarding`, { method: "POST" });
      toast.success("Welcome! Your account is ready.");
      onComplete();
    } catch (e) {
      toast.error("Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="onboarding-wizard">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Progress */}
        <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0]">
          <h2 className="text-xl font-bold text-[#145886] mb-4" data-testid="onboarding-title">Welcome! Let's get you set up</h2>
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${step > s.id ? "bg-[#55B848] text-white" : step === s.id ? "bg-[#145886]/10 text-[#145886] ring-2 ring-[#145886]" : "bg-[#F1F5F9] text-[#94A3B8]"}`}>
                  {step > s.id ? <Check className="w-4 h-4" /> : s.id}
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 rounded ${step > s.id ? "bg-[#55B848]" : "bg-[#E2E8F0]"}`} />}
              </div>
            ))}
          </div>
          <p className="text-sm text-[#475569] mt-3">{STEPS[step - 1].desc}</p>
        </div>

        {/* Content */}
        <div className="p-6 min-h-[280px]">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Full Name *</label>
                  <input value={profileData.full_name} onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0F172A] placeholder-[#94A3B8] focus:border-[#145886] focus:outline-none" placeholder="Enter full name" data-testid="onboarding-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">PAN Number *</label>
                    <input value={profileData.pan_number} onChange={(e) => setProfileData({ ...profileData, pan_number: e.target.value.toUpperCase() })} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0F172A] placeholder-[#94A3B8] focus:border-[#145886] focus:outline-none" placeholder="ABCDE1234F" maxLength={10} data-testid="onboarding-pan" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Aadhaar Number</label>
                    <input value={profileData.aadhar_number} onChange={(e) => setProfileData({ ...profileData, aadhar_number: e.target.value })} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0F172A] placeholder-[#94A3B8] focus:border-[#145886] focus:outline-none" placeholder="1234 5678 9012" maxLength={14} data-testid="onboarding-aadhar" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Date of Birth</label>
                    <input type="date" value={profileData.dob} onChange={(e) => setProfileData({ ...profileData, dob: e.target.value })} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0F172A] focus:border-[#145886] focus:outline-none" data-testid="onboarding-dob" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-1">Phone</label>
                    <input value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#0F172A] placeholder-[#94A3B8] focus:border-[#145886] focus:outline-none" placeholder="+91 98765 43210" data-testid="onboarding-phone" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <p className="text-sm text-[#475569]">Upload your KYC documents. You can skip this and upload later.</p>
                <div className="space-y-3">
                  <div className="bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-lg p-4 hover:border-[#145886]/50 transition-colors">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <FileText className="w-5 h-5 text-[#145886]" />
                      <div className="flex-1">
                        <span className="text-[#0F172A] text-sm font-medium">PAN Card</span>
                        <p className="text-xs text-[#94A3B8]">{panFile ? panFile.name : "PDF, JPG, PNG"}</p>
                      </div>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setPanFile(e.target.files[0])} data-testid="onboarding-pan-upload" />
                      <span className="text-xs px-3 py-1 bg-[#145886]/10 text-[#145886] rounded-full">{panFile ? "Selected" : "Choose"}</span>
                    </label>
                  </div>
                  <div className="bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-lg p-4 hover:border-[#145886]/50 transition-colors">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <FileText className="w-5 h-5 text-[#145886]" />
                      <div className="flex-1">
                        <span className="text-[#0F172A] text-sm font-medium">Aadhaar Card</span>
                        <p className="text-xs text-[#94A3B8]">{aadharFile ? aadharFile.name : "PDF, JPG, PNG"}</p>
                      </div>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setAadharFile(e.target.files[0])} data-testid="onboarding-aadhar-upload" />
                      <span className="text-xs px-3 py-1 bg-[#145886]/10 text-[#145886] rounded-full">{aadharFile ? "Selected" : "Choose"}</span>
                    </label>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <p className="text-sm text-[#475569]">Choose the service you need.</p>
                {[
                  { id: "itr_filing", name: "ITR Filing", desc: "Income Tax Return preparation and filing" },
                  { id: "accounting", name: "Accounting & Bookkeeping", desc: "Transaction recording and financial statements" },
                  { id: "both", name: "Complete Package", desc: "ITR Filing + Accounting services" },
                ].map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${selectedPackage === pkg.id ? "bg-[#145886]/5 border-[#145886] text-[#0F172A]" : "bg-[#F8FAFC] border-[#E2E8F0] text-[#334155] hover:border-[#145886]/30"}`}
                    data-testid={`onboarding-pkg-${pkg.id}`}
                  >
                    <span className="font-bold text-sm">{pkg.name}</span>
                    <p className="text-xs text-[#475569] mt-0.5">{pkg.desc}</p>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <button
            onClick={() => step > 1 && setStep(step - 1)}
            className={`flex items-center gap-1 text-sm font-medium transition-colors ${step > 1 ? "text-[#475569] hover:text-[#145886]" : "text-transparent pointer-events-none"}`}
            data-testid="onboarding-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={step === 1 ? handleCreateProfile : step === 2 ? handleUploadDocs : handleSelectPackage}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#145886] text-white font-bold text-sm rounded-lg hover:bg-[#0e4068] transition-all disabled:opacity-50"
            data-testid="onboarding-next"
          >
            {loading ? "Processing..." : step === 3 ? "Complete Setup" : "Continue"}
            {!loading && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingWizard;
